import { useEffect, useState } from 'react';
import {
  Card,
  Stack,
  TableCell,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Button,
  Menu,
  MenuItem,
  Dialog,
  TextField,
  Tooltip,
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@mui/material';
import { ExpandMore } from '@material-ui/icons';
import { DateCalendar, MonthCalendar } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import { Card404, Iconify, Scrollbar, StatusTag } from '@app/components';
import { Strings } from '@app/constants';
import { useDialog, useRequestState } from '@app/hooks';
import { Header } from '@app/sections/header';
import { InfoStatusCard } from '@app/components/card/info-status-card';
import { SelectedInternsTitle } from '../style';
import { PATH_DASHBOARD } from '@app/routes/paths';
import { selectGetFilteredCandidatesState } from '../selecter';
import { getFilteredCandidates } from '../slice';
import { IInterns } from '../types';
import { AttachmentViewer } from '@app/components/card';

const SelectedUsers = () => {
  const monthPickerDialog = useDialog();
  const datePickerDialog = useDialog();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const [selectedFilter, setSelectedFilter] = useState('');

  const handleFilterOptionClick = (option: string) => {
    setSelectedFilter(option);
    if (option === 'byDate') {
      datePickerDialog.show();
    } else {
      monthPickerDialog.show();
    }
    handleClose();
  };
  const handleClear = () => {
    setSelectedDate(null);
    setSelectedMonth(null);
    setSelectedFilter('');
  };

  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<dayjs.Dayjs | null>(null);
  const formattedDate = dayjs(selectedDate).format('YYYY-MM-DD');
  const formattedMonth = dayjs(selectedMonth).format('MMMM');
  const handleDateChange = (newValue: dayjs.Dayjs | null) => {
    setSelectedDate(newValue);
    datePickerDialog.hide();
  };

  const handleMonthChange = (newValue: dayjs.Dayjs | null) => {
    setSelectedMonth(newValue);
    monthPickerDialog.hide();
  };

  const viewResumeDialog = useDialog();
  const dispatch = useDispatch();
  const currentMonth = new Date().toLocaleString('default', { month: 'long' });

  useEffect(() => {
    let params = {};
    if (selectedFilter === 'byDate' && formattedDate !== 'Invalid Date') {
      params = { formattedDate };
    } else if (selectedFilter === 'byMonth' && formattedMonth !== 'Invalid Date') {
      params = { formattedMonth };
    } else {
      params = { currentMonth };
    }

    if (Object.keys(params).length > 0) {
      dispatch(getFilteredCandidates(params));
    }
  }, [dispatch, formattedDate, formattedMonth, selectedFilter, currentMonth]);
  const { data } = useRequestState({
    stateSelector: selectGetFilteredCandidatesState,
  });
  const navigate = useNavigate();

  const getStatus = (candidate: IInterns) => {
    return data?.selectedEmployees?.some(
      (employees: IInterns) => employees.userId === candidate.userId
    )
      ? 'SelectedAsEmployee'
      : data?.internFinalWeek?.some((intern: IInterns) => intern.userId === candidate.userId)
      ? 'InternFinalWeek'
      : data?.rejectedCandidates?.some(
          (rejected: IInterns) => rejected?.userId === candidate?.userId
        )
      ? 'Rejected'
      : data?.selectedInterns?.some((intern: IInterns) => intern?.userId === candidate?.userId)
      ? 'SelectedAsIntern'
      : 'Candidate';
  };

  return (
    <Card sx={{ mb: 2, borderRadius: '5px', ml: { xs: 1, md: 2 }, mr: { xs: 1, md: 2 } }}>
      <Stack flexDirection={{ sm: 'column', md: 'row' }}>
        <Card
          sx={{
            m: { xs: 0, md: 2 },
            borderRadius: '5px',
            width: { md: '43%', xs: '100%' },
            boxShadow: { xs: 0, md: 1 },
          }}
        >
          <Stack direction={'row'} justifyContent={'space-between'} p={1.5}>
            <Stack fontSize={17} fontWeight={700} justifyContent={'center'}>
              {Strings.page_title.candidates}
            </Stack>
            <Stack width={'32%'} mb={1} p={-1}>
              {!selectedDate && !selectedMonth && (
                <TextField name="input" value={currentMonth} disabled size="small" />
              )}
              {selectedFilter === 'byDate' && selectedDate && (
                <TextField name="input" value={formattedDate} disabled size="small" />
              )}
              {selectedFilter === 'byMonth' && selectedMonth && (
                <TextField name="input" value={formattedMonth} disabled size="small" />
              )}
            </Stack>
            {selectedDate ?? selectedMonth ? (
              <Tooltip title="Clear Filter">
                <Iconify
                  icon="tdesign:filter-clear"
                  onClick={handleClear}
                  sx={{ cursor: 'pointer', mr: 3, mt: 1 }}
                />
              </Tooltip>
            ) : (
              <Iconify
                icon="octicon:filter-16"
                onClick={handleClick}
                sx={{ cursor: 'pointer', mr: 3, mt: 1 }}
              />
            )}
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem
                sx={{ fontSize: 13 }}
                onClick={() => {
                  handleFilterOptionClick('byDate');
                }}
              >
                {Strings.dashboard.byDate}
              </MenuItem>
              <MenuItem
                sx={{ fontSize: 13 }}
                onClick={() => {
                  handleFilterOptionClick('byMonth');
                }}
              >
                {Strings.dashboard.byMonth}
              </MenuItem>
            </Menu>
          </Stack>
          <Scrollbar
            sx={{
              height: 370,
              '&::-webkit-scrollbar': { display: 'none' },
              scrollbarWidth: 'none',
              '-ms-overflow-style': 'none',
            }}
          >
            <Table aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell align="center">{Strings.field.name}</TableCell>
                  <TableCell align="center">{Strings.dashboard.applyDate}</TableCell>
                  <TableCell align="center">{Strings.field.status}</TableCell>
                  <TableCell align="center">{Strings.field.resume}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.candidates?.length > 0 ? (
                  data?.candidates?.map((candidate: IInterns, index: number) => {
                    const openDetailsPage = () => {
                      navigate(`${PATH_DASHBOARD.candidateDetail}/${candidate.firstName}`, {
                        state: { id: candidate.userId },
                      });
                    };
                    return (
                      <TableRow key={index}>
                        <TableCell
                          align="center"
                          onClick={openDetailsPage}
                        >{`${candidate.user.firstName} ${candidate.user.lastName}`}</TableCell>
                        <TableCell align="center" onClick={openDetailsPage}>
                          {dayjs(candidate.promotionDate).format('DD/MM/YYYY')}
                        </TableCell>
                        <TableCell align="center" onClick={openDetailsPage}>
                          <StatusTag status={getStatus(candidate)} />
                        </TableCell>
                        <TableCell align="center">
                          <Button onClick={viewResumeDialog.show}> {'Link'}</Button>
                        </TableCell>
                        {viewResumeDialog.visible && (
                          <AttachmentViewer
                            open
                            onClose={viewResumeDialog.hide}
                            resume={candidate?.user.resume}
                            key={index}
                          />
                        )}
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow sx={{ '& td, & th': { borderBottom: 'none' } }}>
                    <TableCell colSpan={4}>
                      <Card404 height={240} message="Data Not Found!" />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            {datePickerDialog.visible && (
              <Dialog
                open
                PaperProps={{
                  style: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '300px',
                  },
                }}
                onClose={() => {
                  datePickerDialog.hide();
                }}
              >
                <Header variant="dialog" title={'Select date'} onClose={datePickerDialog.hide} />
                <DateCalendar onChange={handleDateChange} />
              </Dialog>
            )}
            {monthPickerDialog.visible && (
              <Dialog
                open
                PaperProps={{
                  style: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '300px',
                  },
                }}
                onClose={() => {
                  monthPickerDialog.hide();
                }}
              >
                <Header variant="dialog" title={'Select Month'} onClose={monthPickerDialog.hide} />
                <MonthCalendar value={selectedMonth} onChange={handleMonthChange} />
              </Dialog>
            )}
          </Scrollbar>
        </Card>
        <Stack sx={{ width: { md: '57%', sm: '100%' }, mt: 1 }}>
          <Scrollbar sx={{ maxHeight: 450 }}>
            <Card elevation={15} sx={{ mt: 1, mx: 1 }}>
              <Accordion>
                <AccordionSummary
                  sx={{
                    padding: 0,
                    marginY: '0px',
                    '&.Mui-expanded': {
                      marginY: '-20px',
                      pt: 2,
                    },
                    px: 2,
                  }}
                  expandIcon={<ExpandMore />}
                  aria-controls="panel1-content"
                  id="panel1-header"
                >
                  <SelectedInternsTitle>{Strings.dashboard.selectedInters}</SelectedInternsTitle>
                </AccordionSummary>
                <AccordionDetails>
                  {data?.selectedInterns?.map((option: IInterns) => {
                    const openDetailsPage = () => {
                      navigate(`${PATH_DASHBOARD.internDetail}/${option.firstName}`, {
                        state: { id: option?.userId },
                      });
                    };
                    return (
                      <InfoStatusCard
                        key={option?.id}
                        titleName={`${option.user.firstName} ${option.user.lastName}`}
                        subTitleHead={`${Strings.dashboard.selectionDate}:`}
                        subTitleValue={dayjs(option?.actionTime).format('DD/MM/YYYY')}
                        actionHead={`${Strings.dashboard.selectedBy} :`}
                        actionSubValue={option?.user?.interviewedBy?.interviewedBy}
                        status={option?.status}
                        onClick={openDetailsPage}
                      />
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            </Card>
            <Card elevation={15} sx={{ mt: 1, mx: 1 }}>
              <Accordion>
                <AccordionSummary
                  sx={{
                    padding: 0,
                    marginY: '0px',
                    '&.Mui-expanded': {
                      marginY: '-20px',
                      pt: 2,
                    },
                    px: 2,
                  }}
                  expandIcon={<ExpandMore />}
                  aria-controls="panel1-content"
                  id="panel1-header"
                >
                  <SelectedInternsTitle>{Strings.dashboard.selectedEmployees}</SelectedInternsTitle>
                </AccordionSummary>
                <AccordionDetails>
                  {data?.selectedEmployees?.map((option: IInterns) => {
                    const openDetailsPage = () => {
                      navigate(`${PATH_DASHBOARD.employeeDetail}/${option.firstName}`, {
                        state: { id: option?.userId },
                      });
                    };
                    return (
                      <InfoStatusCard
                        key={option?.id}
                        titleName={`${option.user.firstName} ${option.user.lastName}`}
                        subTitleHead={`${Strings.dashboard.selectionDate}:`}
                        subTitleValue={dayjs(option?.actionTime).format('DD/MM/YYYY')}
                        actionHead={`${Strings.dashboard.selectedBy} :`}
                        actionSubValue={option?.user?.interviewedBy?.interviewedBy}
                        status={option?.status}
                        onClick={openDetailsPage}
                      />
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            </Card>
            <Card elevation={15} sx={{ mt: 1, mx: 1 }}>
              <Accordion>
                <AccordionSummary
                  sx={{
                    padding: 0,
                    marginY: '0px',
                    '&.Mui-expanded': {
                      marginY: '-20px',
                      pt: 2,
                    },
                    px: 2,
                  }}
                  expandIcon={<ExpandMore />}
                  aria-controls="panel1-content"
                  id="panel1-header"
                >
                  <SelectedInternsTitle>{Strings.status.rejected}</SelectedInternsTitle>
                </AccordionSummary>
                <AccordionDetails>
                  {data?.rejectedCandidates?.map((option: IInterns) => {
                    const openDetailsPage = () => {
                      navigate(`${PATH_DASHBOARD.candidateDetail}/${option.firstName}`, {
                        state: { id: option?.userId },
                      });
                    };
                    return (
                      <InfoStatusCard
                        key={option?.id}
                        titleName={`${option.user.firstName} ${option.user.lastName}`}
                        status={option?.status}
                        actionHead={`${Strings.dashboard.rejectedBy} :`}
                        actionSubValue={option?.user?.interviewedBy?.interviewedBy}
                        subTitleHead={`${Strings.dashboard.rejectionDate}:`}
                        subTitleValue={dayjs(option?.promotionDate).format('DD/MM/YYYY')}
                        description={option?.user?.reason}
                        onClick={openDetailsPage}
                      />
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            </Card>
          </Scrollbar>
        </Stack>
      </Stack>
    </Card>
  );
};

export { SelectedUsers };
